import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Avatar, Chip, Card, CardContent,
  TextField, Select, MenuItem, FormControl, InputLabel, Slider,
  Divider, Alert, CircularProgress, Tooltip,
  Snackbar,
  useMediaQuery, useTheme, Drawer, LinearProgress,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Schedule as ScheduleIcon, DirectionsCar as DriveIcon, Restaurant as LunchIcon,
  Home as HomeIcon, MyLocation as GpsIcon, Refresh as RefreshIcon,
  DragIndicator as DragIcon,
  AccessTime as TimeIcon, Place as PlaceIcon,
  ChevronLeft, ChevronRight,
  Today as TodayIcon, Map as MapIcon,
  Close as CloseIcon,
  SwapVert as ReorderIcon, FlagCircle as FlagIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import {
  MapContainer, Marker, Popup, Polyline, useMap, L,
} from '../components/map/MapLibreCompat';
import { format, addDays, subDays, parseISO, isToday } from 'date-fns';
import api from '../services/api';
import { useWSRefresh, useVisibilityRefresh } from '../contexts/WebSocketContext';

// ── Types ────────────────────────────────────────────────────────────────

interface ScheduleStop {
  stop_type: 'job' | 'lunch' | 'end_of_day';
  ticket_id: number | null;
  title: string;
  address: string;
  lat: number | null;
  lng: number | null;
  estimated_duration_minutes: number;
  travel_from_previous_seconds: number;
  travel_from_previous_miles: number;
  travel_from_previous_polyline: [number, number][];
  depart_at: string | null;
  arrive_at: string | null;
  finish_at: string | null;
  notes: string;
}

interface DailySchedule {
  tech_id: number;
  tech_name: string;
  schedule_date: string;
  day_start: string;
  day_end: string;
  lunch_after_stop: number;
  lunch_time: string | null;
  lunch_duration_minutes: number;
  end_destination: string;
  end_destination_address: string;
  start_lat: number | null;
  start_lng: number | null;
  start_address: string;
  stops: ScheduleStop[];
  total_drive_miles: number;
  total_drive_minutes: number;
  total_work_minutes: number;
  warnings: string[];
}

interface Technician {
  id: number;
  name: string;
  initials: string;
  color: string;
  jobs_today: number;
  latitude: number | null;
  longitude: number | null;
  status: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────

const fmtTime = (iso: string | null) => {
  if (!iso) return '--:--';
  try { return format(parseISO(iso), 'h:mm a'); } catch { return '--:--'; }
};
const fmtDuration = (seconds: number) => {
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};
const fmtMiles = (mi: number) => mi < 0.1 ? 'same location' : `${mi.toFixed(1)} mi`;

const stopColors: Record<string, string> = {
  job: '#1976d2',
  lunch: '#ff9800',
  end_of_day: '#4caf50',
};

const createStopMarker = (type: string, index: number) => L.divIcon({
  className: 'schedule-marker',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
  html: `<div style="
    width:32px;height:32px;
    background:${stopColors[type] || '#666'};
    border:3px solid white;
    border-radius:50% 50% 50% 0;
    transform:rotate(-45deg);
    box-shadow:0 2px 6px rgba(0,0,0,.35);
    display:flex;align-items:center;justify-content:center;
    color:white;font-weight:bold;font-size:13px;
  "><span style="transform:rotate(45deg)">${type === 'lunch' ? '🍽' : type === 'end_of_day' ? '🏠' : index + 1}</span></div>`,
});

const createStartMarker = () => L.divIcon({
  className: 'schedule-marker',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -18],
  html: `<div style="
    width:36px;height:36px;background:#4caf50;border:3px solid white;
    border-radius:50%;display:flex;align-items:center;justify-content:center;
    color:white;font-weight:bold;font-size:16px;box-shadow:0 2px 8px rgba(0,0,0,.4);
  ">📍</div>`,
});

// ── Map auto-fit component ───────────────────────────────────────────────

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || !points.length) return;
    map.fitBounds(L.latLngBounds(points).pad(0.15), { maxZoom: 13 });
    fitted.current = true;
  }, [points, map]);
  // Reset when points change significantly
  useEffect(() => { fitted.current = false; }, [points.length]);
  return null;
}

// ── Route colors (one per leg) ───────────────────────────────────────────

const routeColors = ['#1976d2', '#9c27b0', '#00897b', '#e65100', '#c62828', '#283593', '#2e7d32', '#ad1457'];

// ═════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════

export default function SchedulePage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ── State ──
  // const [dateEnd, setDateEnd] = useState(''); // date range - future feature
  const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [techs, setTechs] = useState<Technician[]>([]);
  const [selectedTechId, setSelectedTechId] = useState<number | null>(null);
  const [schedule, setSchedule] = useState<DailySchedule | null>(null);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState('');
  const [snackMsg, setSnackMsg] = useState('');

  // Schedule config
  const [dayStart, setDayStart] = useState('08:00');
  const [dayEnd, setDayEnd] = useState('17:00');
  const [lunchMode, setLunchMode] = useState<'smart' | 'after_stop' | 'fixed_time'>('smart');
  const [timeframe, setTimeframe] = useState<'day'|'multi'|'all'>('day');
  const [rangeEnd, setRangeEnd] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [flagFilter, setFlagFilter] = useState<{missed:boolean;needs_return:boolean;aged:boolean}>({missed:false,needs_return:false,aged:false});
  const [windowTickets, setWindowTickets] = useState<any[]>([]);
  const [lunchAfterStop, setLunchAfterStop] = useState(2);
  const [lunchTime, setLunchTime] = useState('12:00');
  const [lunchDuration, setLunchDuration] = useState(30);
  const [endDest, setEndDest] = useState<'home' | 'custom' | 'none'>('home');
  const [customDestAddr, setCustomDestAddr] = useState('');

  // Drag reorder state (simple index swap)
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<'closest'|'furthest'|'custom'>('custom');
  const [jobOrder, setJobOrder] = useState<number[]>([]);

  // Map
  const [showMap, setShowMap] = useState(!isMobile);

  // ── Load technicians ──
  
  // WebSocket push — instant refresh on schedule changes and GPS updates
  // Ticket/schedule changes - full reload
  useWSRefresh(['schedule:updated', 'ticket:assigned', 'ticket:status'], () => { loadTechs(); });
  // GPS push - instant inline update with speed
  useWSRefresh(['tracking:location'], (data: any) => {
    if (data?.updated_users?.length) {
      setTechs(prev => {
        const updated = [...prev];
        for (const u of data.updated_users) {
          const idx = updated.findIndex(t => t.id === u.user_id);
          if (idx >= 0) {
            updated[idx] = {
              ...updated[idx],
              latitude: u.latitude,
              longitude: u.longitude,
              status: (u.speed > 0.5) ? 'en_route' : updated[idx].status === 'offline' ? 'available' : updated[idx].status,
            };
          }
        }
        return updated;
      });
    }
  });
  // Refresh immediately when tab becomes visible
  useVisibilityRefresh(() => { loadTechs(); });

  useEffect(() => {
    loadTechs();
    const iv = setInterval(() => loadTechsRef.current(), 30000);
    return () => clearInterval(iv);
  }, []);

  const loadTechsRef = useRef<() => void>(() => {});
  useEffect(() => { loadTechsRef.current = loadTechs; });
  const loadTechs = async () => {
    try {
      const res = await api.get('/tracking/locations/live');
      const data = res.data?.technicians || res.data || [];
      const mapped = data.map((t: any) => ({
        id: t.id,
        name: t.name || `${t.first_name || ''} ${t.last_name || ''}`.trim(),
        initials: t.initials || (t.name || '').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase(),
        color: t.color || '#14548C',
        jobs_today: t.jobs_today || 0,
        latitude: t.latitude,
        longitude: t.longitude,
        status: t.status || 'offline',
      }));
      setTechs(mapped);
      if (mapped.length > 0 && !selectedTechId) {
        setSelectedTechId(mapped[0].id);
      }
    } catch (e: any) {
      // Fallback: try technicians endpoint
      try {
        const res = await api.get('/technicians');
        const data = res.data || [];
        const mapped = data.map((t: any) => ({
          id: t.id,
          name: t.full_name || t.name || `${t.first_name || ''} ${t.last_name || ''}`.trim(),
          initials: (t.full_name || t.name || '').split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase(),
          color: '#14548C',
          jobs_today: 0,
          latitude: null,
          longitude: null,
          status: 'offline',
        }));
        setTechs(mapped);
        if (mapped.length > 0 && !selectedTechId) setSelectedTechId(mapped[0].id);
      } catch { }
    }
  };

  // ── Build schedule ──
  const buildSchedule = useCallback(async (ticketIds?: number[]) => {
    if (!selectedTechId) return;
    setBuilding(true);
    setError('');
    try {
      const tech = techs.find(t => t.id === selectedTechId);
      const req: any = {
        tech_id: selectedTechId,
        schedule_date: selectedDate,
        day_start: dayStart,
        day_end: dayEnd,
        lunch_mode: lunchMode === 'smart' ? 'smart' : lunchMode === 'fixed_time' ? 'fixed' : 'positional',
        lunch_after_stop: lunchMode === 'after_stop' ? lunchAfterStop : 0,
        lunch_time: lunchMode === 'fixed_time' ? lunchTime : null,
        lunch_duration_minutes: lunchDuration,
        end_destination: endDest,
        include_polylines: true,
      };
      if (ticketIds) req.ticket_ids = ticketIds;
      // Use live GPS if available
      if (tech?.latitude && tech?.longitude) {
        req.start_lat = tech.latitude;
        req.start_lng = tech.longitude;
      }
      if (endDest === 'custom' && customDestAddr) {
        req.end_destination_address = customDestAddr;
      }

      const res = await api.post('/schedule/build', req);
      setSchedule(res.data);
      // Update job order from response
      const ids = (res.data.stops || [])
        .filter((s: ScheduleStop) => s.stop_type === 'job' && s.ticket_id)
        .map((s: ScheduleStop) => s.ticket_id as number);
      setJobOrder(ids);
      if (res.data.warnings?.length) {
        setSnackMsg(`⚠️ ${res.data.warnings.length} warning(s)`);
      }
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to build schedule');
    } finally {
      setBuilding(false);
    }
  }, [selectedTechId, selectedDate, dayStart, dayEnd, lunchMode, lunchAfterStop, lunchTime, lunchDuration, endDest, customDestAddr, techs]);

  // ── Auto-load when tech or date changes ──
  useEffect(() => {
    if (selectedTechId) buildSchedule();
  }, [selectedTechId, selectedDate]);

  // ── Optimize route order ──
  const optimizeOrder = async () => {
    if (!selectedTechId || !schedule || jobOrder.length < 2) return;
    setBuilding(true);
    try {
      const tech = techs.find(t => t.id === selectedTechId);
      const startLat = schedule.start_lat || tech?.latitude || 0;
      const startLng = schedule.start_lng || tech?.longitude || 0;
      const res = await api.post('/schedule/optimize-order', {
        tech_id: selectedTechId,
        schedule_date: selectedDate,
        ticket_ids: jobOrder,
        start_lat: startLat,
        start_lng: startLng,
      });
      const optimized = res.data.optimized_ticket_ids;
      setSnackMsg('Route optimized! Rebuilding schedule...');
      await buildSchedule(optimized);
    } catch (e: any) {
      setError('Optimization failed');
    } finally {
      setBuilding(false);
    }
  };

  // ── Drag reorder ──
  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const newOrder = [...jobOrder];
    const [moved] = newOrder.splice(dragIdx, 1);
    newOrder.splice(idx, 0, moved);
    setJobOrder(newOrder);
    setDragIdx(idx);
  };
  const handleDragEnd = () => {
    setDragIdx(null);
    // Rebuild with new order
    buildSchedule(jobOrder);
  };

  // ── Date navigation ──
  const goToday = () => setSelectedDate(format(new Date(), 'yyyy-MM-dd'));
  const sortJobs = (order: 'closest'|'furthest'|'custom') => {
    setSortOrder(order);
    if (order === 'custom' || !schedule) return;
    const jobIds = schedule.stops.filter(s => s.stop_type === 'job' && s.ticket_id).map(s => s.ticket_id!);
    if (order === 'closest') {
      jobIds.sort((a, b) => {
        const sa = schedule.stops.find(s => s.ticket_id === a);
        const sb = schedule.stops.find(s => s.ticket_id === b);
        return (sa?.travel_from_previous_miles || 0) - (sb?.travel_from_previous_miles || 0);
      });
    } else {
      jobIds.sort((a, b) => {
        const sa = schedule.stops.find(s => s.ticket_id === a);
        const sb = schedule.stops.find(s => s.ticket_id === b);
        return (sb?.travel_from_previous_miles || 0) - (sa?.travel_from_previous_miles || 0);
      });
    }
    setJobOrder(jobIds);
  };

  useEffect(() => {
    if (timeframe === 'day') { setWindowTickets([]); return; }
    const params: any = { page_size: 500 };
    if (selectedTechId) params.assigned_to_id = selectedTechId;
    if (timeframe === 'multi') { params.scheduled_from = `${selectedDate}T00:00:00`; params.scheduled_to = `${rangeEnd}T23:59:59`; }
    const fl = Object.entries(flagFilter).filter(([, on]) => on).map(([k]) => k);
    if (fl.length) params.flags = fl.join(',');
    api.get('/tickets', { params }).then(r => setWindowTickets(r.data?.items || [])).catch(() => setWindowTickets([]));
  }, [timeframe, selectedDate, rangeEnd, selectedTechId, flagFilter]);

  const goPrev = () => setSelectedDate(format(subDays(parseISO(selectedDate), 1), 'yyyy-MM-dd'));
  const goNext = () => setSelectedDate(format(addDays(parseISO(selectedDate), 1), 'yyyy-MM-dd'));

  // ── Map points ──
  const mapPoints = useMemo((): [number, number][] => {
    if (!schedule) return [];
    const pts: [number, number][] = [];
    if (schedule.start_lat && schedule.start_lng) pts.push([schedule.start_lat, schedule.start_lng]);
    schedule.stops.forEach(s => { if (s.lat && s.lng) pts.push([s.lat, s.lng]); });
    return pts;
  }, [schedule]);

  // Job stops only (for numbering)
  const [returnTripIds, setReturnTripIds] = useState<Set<number>>(new Set());
  useEffect(() => {
    api.get('/tickets', { params: { status: 'assigned,en_route,on_site,in_progress,pending_parts', page_size: 500 } })
      .then(r => setReturnTripIds(new Set((r.data?.items || []).filter((t: any) => t.return_trip_required).map((t: any) => t.id))))
      .catch(() => {});
  }, [schedule]);

  const jobStops = useMemo(() =>
    (schedule?.stops || []).filter(s => s.stop_type === 'job'),
    [schedule]
  );

  const selectedTech = techs.find(t => t.id === selectedTechId);

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* ── Top bar ── */}
      <Paper elevation={0} sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <ScheduleIcon color="primary" />
        <Typography variant="h6" fontWeight={700} sx={{ mr: 1 }}>Schedule Builder</Typography>

        {/* Date navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={goPrev}><ChevronLeft /></IconButton>
          <TextField type="date" size="small" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
            sx={{ width: 160 }} InputLabelProps={{ shrink: true }} />
          <IconButton size="small" onClick={goNext}><ChevronRight /></IconButton>
          <Button size="small" variant={isToday(parseISO(selectedDate)) ? 'contained' : 'outlined'} onClick={goToday}
            startIcon={<TodayIcon />} sx={{ ml: 0.5 }}>Today</Button>
        </Box>

        {/* Timeframe: single day / multiple days / all */}
        <ToggleButtonGroup size="small" exclusive value={timeframe} onChange={(_, v) => v && setTimeframe(v)}>
          <ToggleButton value="day">Single day</ToggleButton>
          <ToggleButton value="multi">Multi-day</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        {timeframe === 'multi' && (
          <TextField type="date" size="small" label="Through" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)}
            sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
        )}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Chip size="small" label="Missed" color={flagFilter.missed ? 'error' : 'default'} variant={flagFilter.missed ? 'filled' : 'outlined'} onClick={() => setFlagFilter(f => ({ ...f, missed: !f.missed }))} />
          <Chip size="small" label="Needs return" color={flagFilter.needs_return ? 'warning' : 'default'} variant={flagFilter.needs_return ? 'filled' : 'outlined'} onClick={() => setFlagFilter(f => ({ ...f, needs_return: !f.needs_return }))} />
          <Chip size="small" label="Aged" color={flagFilter.aged ? 'secondary' : 'default'} variant={flagFilter.aged ? 'filled' : 'outlined'} onClick={() => setFlagFilter(f => ({ ...f, aged: !f.aged }))} />
        </Box>

        {/* Tech selector */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Technician</InputLabel>
          <Select value={selectedTechId || ''} label="Technician"
            onChange={e => setSelectedTechId(Number(e.target.value))}>
            {techs.map(t => (
              <MenuItem key={t.id} value={t.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: t.color }}>{t.initials}</Avatar>
                  {t.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        {/* Actions */}
        <Tooltip title="Ask this technician's phone for a GPS fix every 5 seconds for the next 5 minutes (each press adds 5 minutes)">
          <span><Button size="small" variant="outlined" color="warning" disabled={!selectedTechId} onClick={async () => {
            try { const r = await api.post(`/tracking/burst/${selectedTechId}`); const u = r.data?.burst_until ? new Date(r.data.burst_until) : null;
              setSnackMsg(`⚡ 5-second GPS updates on until ${u ? u.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'in 5 min'} (phone picks it up within a minute)`); }
            catch (e: any) { setSnackMsg(e?.response?.data?.detail || 'Could not start 5-second updates'); } }}>⚡ 5-sec GPS</Button></span>
        </Tooltip>
        <Button size="small" variant="outlined" startIcon={<ReorderIcon />} onClick={optimizeOrder}
          disabled={building || jobStops.length < 2}>Optimize Route</Button>
        <Chip label="Closest" size="small" variant={sortOrder==='closest'?'filled':'outlined'} color="primary"
                      onClick={() => sortJobs('closest')} sx={{ cursor: 'pointer', mr: 0.5 }} />
          <Chip label="Furthest" size="small" variant={sortOrder==='furthest'?'filled':'outlined'} color="primary"
            onClick={() => sortJobs('furthest')} sx={{ cursor: 'pointer', mr: 0.5 }} />
          <Chip label="Custom" size="small" variant={sortOrder==='custom'?'filled':'outlined'} color="default"
            onClick={() => setSortOrder('custom')} sx={{ cursor: 'pointer', mr: 1 }} />
          <Button variant="contained" size="small"
            onClick={() => buildSchedule()} disabled={building}>
          {building ? 'Building...' : 'Rebuild'}
        </Button>
        {isMobile && (
          <IconButton onClick={() => setShowMap(!showMap)} color={showMap ? 'primary' : 'default'}>
            <MapIcon />
          </IconButton>
        )}
      </Paper>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mx: 2, mt: 1 }}>{error}</Alert>}

      {/* ── Multi-day / All ticket list ── */}
      {timeframe !== 'day' && (
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {timeframe === 'multi' ? `Tickets ${selectedDate} → ${rangeEnd}` : 'All tickets'}
            {selectedTechId ? ` · ${techs.find(t => t.id === selectedTechId)?.name || ''}` : ''} ({windowTickets.length})
          </Typography>
          {windowTickets.length === 0 ? (
            <Typography color="text.secondary" variant="body2">No tickets match this timeframe/filter.</Typography>
          ) : windowTickets.map((t: any) => (
            <Box key={t.id} onClick={() => navigate(`/tickets?id=${t.id}`)}
              sx={{ p: 1.25, mb: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' }, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#14548C', minWidth: 130 }}>{t.ticket_number}</Typography>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }}>{t.title}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>{t.scheduled_start ? format(parseISO(t.scheduled_start), 'MMM d, h:mm a') : 'Unscheduled'}</Typography>
              {t.missed && <Chip size="small" label="MISSED" color="error" />}
              {(t.needs_return ?? t.return_trip_required) && <Chip size="small" label="NEEDS RETURN" color="warning" />}
              {t.aged && <Chip size="small" label={`AGED${t.aged_days ? ' ' + t.aged_days + 'd' : ''}`} color="secondary" />}
            </Box>
          ))}
        </Box>
      )}

      {/* ── Main content ── */}
      <Box sx={{ flex: 1, display: timeframe === 'day' ? 'flex' : 'none', overflow: 'hidden' }}>

        {/* ── LEFT: Schedule timeline ── */}
        <Box sx={{ width: isMobile ? '100%' : showMap ? '55%' : '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: showMap ? '1px solid' : 'none', borderColor: 'divider' }}>

          {/* Config bar */}
          <Paper elevation={0} sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField label="Start" type="time" size="small" value={dayStart}
              onChange={e => setDayStart(e.target.value)} sx={{ width: 110 }} InputLabelProps={{ shrink: true }} />
            <TextField label="End" type="time" size="small" value={dayEnd}
              onChange={e => setDayEnd(e.target.value)} sx={{ width: 110 }} InputLabelProps={{ shrink: true }} />
            <Divider orientation="vertical" flexItem />

            {/* Lunch controls */}
            <LunchIcon sx={{ color: '#ff9800' }} />
            <ToggleButtonGroup size="small" exclusive value={lunchMode} onChange={(_, v) => v && setLunchMode(v)}>
              <ToggleButton value="smart">Smart (CA)</ToggleButton>
              <ToggleButton value="after_stop">After job #</ToggleButton>
              <ToggleButton value="fixed_time">Fixed time</ToggleButton>
            </ToggleButtonGroup>
            {lunchMode === 'smart' ? (
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 180 }}>
                Meal breaks auto-placed per CA law (1st by 5th hr, 2nd by 10th hr on shifts &gt; 10h).
              </Typography>
            ) : lunchMode === 'after_stop' ? (
              <TextField label="After job" type="number" size="small" value={lunchAfterStop}
                onChange={e => setLunchAfterStop(Number(e.target.value))} sx={{ width: 80 }}
                InputProps={{ inputProps: { min: 1, max: 20 } }} />
            ) : (
              <TextField label="Lunch at" type="time" size="small" value={lunchTime}
                onChange={e => setLunchTime(e.target.value)} sx={{ width: 110 }} InputLabelProps={{ shrink: true }} />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Duration:</Typography>
              <Slider value={lunchDuration} onChange={(_, v) => setLunchDuration(v as number)}
                min={15} max={90} step={5} valueLabelDisplay="auto"
                valueLabelFormat={v => `${v}m`} sx={{ width: 80 }} />
              <Typography variant="body2" fontWeight={600}>{lunchDuration}m</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />

            {/* End of day */}
            <FlagIcon sx={{ color: '#4caf50' }} />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>End of day</InputLabel>
              <Select value={endDest} label="End of day" onChange={e => setEndDest(e.target.value as any)}>
                <MenuItem value="home">🏠 Route home</MenuItem>
                <MenuItem value="custom">📍 Custom destination</MenuItem>
                <MenuItem value="none">⏹ End at last job</MenuItem>
              </Select>
            </FormControl>
            {endDest === 'custom' && (
              <TextField label="Destination address" size="small" value={customDestAddr}
                onChange={e => setCustomDestAddr(e.target.value)} sx={{ minWidth: 200 }} />
            )}
          </Paper>

          {/* Summary stats */}
          {schedule && (
            <Paper elevation={0} sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: selectedTech?.color || '#14548C' }}>
                  {selectedTech?.initials}
                </Avatar>
                <Typography variant="subtitle2" fontWeight={600}>{schedule.tech_name}</Typography>
              </Box>
              <Chip icon={<PlaceIcon />} label={`${jobStops.length} jobs`} size="small" color="primary" variant="outlined" />
              <Chip icon={<DriveIcon />} label={`${schedule.total_drive_miles} mi · ${fmtDuration(schedule.total_drive_minutes * 60)}`} size="small" variant="outlined" />
              <Chip icon={<TimeIcon />} label={`${fmtDuration(schedule.total_work_minutes * 60)} work`} size="small" variant="outlined" />
              {schedule.start_address && (
                <Chip icon={<GpsIcon />} label={schedule.start_address} size="small" variant="outlined"
                  sx={{ maxWidth: 250, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
              )}
            </Paper>
          )}

          {/* Warnings */}
          {schedule?.warnings?.length ? (
            <Box sx={{ px: 2, py: 0.5 }}>
              {schedule.warnings.map((w, i) => (
                <Alert severity="warning" key={i} sx={{ mb: 0.5, py: 0 }} variant="outlined">
                  <Typography variant="caption">{w}</Typography>
                </Alert>
              ))}
            </Box>
          ) : null}

          {/* Loading */}
          {building && <LinearProgress />}

          {/* ── Timeline ── */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {!schedule && !building && (
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                <ScheduleIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                <Typography variant="h6">Select a technician to build their schedule</Typography>
                <Typography variant="body2">Travel times will be calculated automatically via OSRM</Typography>
              </Box>
            )}

            {schedule && schedule.stops.length === 0 && !building && (
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                <Typography variant="h6">No jobs scheduled</Typography>
                <Typography variant="body2">Assign tickets to {schedule.tech_name} for {format(parseISO(selectedDate), 'MMM d, yyyy')}</Typography>
              </Box>
            )}

            {schedule && schedule.stops.map((stop, idx) => {
              const jobNum = stop.stop_type === 'job'
                ? jobStops.findIndex(j => j.ticket_id === stop.ticket_id) + 1
                : 0;
              const jobDragIdx = stop.stop_type === 'job'
                ? jobOrder.indexOf(stop.ticket_id!)
                : -1;
              const color = stopColors[stop.stop_type] || '#666';

              return (
                <Box key={`${stop.stop_type}-${stop.ticket_id || idx}`}>
                  {/* Travel segment */}
                  {stop.travel_from_previous_seconds > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2.5, py: 0.5 }}>
                      <Box sx={{ width: 2, height: 32, bgcolor: 'divider', ml: '14px' }} />
                      <DriveIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.secondary">
                        {fmtDuration(stop.travel_from_previous_seconds)} · {fmtMiles(stop.travel_from_previous_miles)} drive
                      </Typography>
                      {stop.depart_at && (
                        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', mr: 1 }}>
                          depart {fmtTime(stop.depart_at)}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Stop card */}
                  <Card
                    elevation={0}
                    draggable={stop.stop_type === 'job'}
                    onDragStart={() => stop.stop_type === 'job' && handleDragStart(jobDragIdx)}
                    onDragOver={e => stop.stop_type === 'job' && handleDragOver(e, jobDragIdx)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      border: '1px solid',
                      borderColor: dragIdx === jobDragIdx && stop.stop_type === 'job' ? 'primary.main' : 'divider',
                      borderLeft: `4px solid ${color}`,
                      mb: 0.5,
                      cursor: stop.stop_type === 'job' ? 'grab' : 'default',
                      transition: 'box-shadow 0.2s, border-color 0.2s',
                      '&:hover': { boxShadow: 2 },
                      bgcolor: stop.stop_type === 'lunch' ? 'rgba(255,152,0,0.04)' :
                               stop.stop_type === 'end_of_day' ? 'rgba(76,175,80,0.04)' : 'background.paper',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        {/* Drag handle / icon */}
                        {stop.stop_type === 'job' ? (
                          <Tooltip title="Drag to reorder">
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 0.5 }}>
                              <DragIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                              <Avatar sx={{ width: 28, height: 28, fontSize: 13, bgcolor: color, mt: 0.5 }}>
                                {jobNum}
                              </Avatar>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Avatar sx={{ width: 28, height: 28, bgcolor: color, mt: 0.5 }}>
                            {stop.stop_type === 'lunch' ? <LunchIcon sx={{ fontSize: 16 }} /> : <HomeIcon sx={{ fontSize: 16 }} />}
                          </Avatar>
                        )}

                        {/* Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" fontWeight={600} noWrap
                              sx={{ cursor: stop.stop_type === 'job' ? 'pointer' : 'default', '&:hover': stop.stop_type === 'job' ? { textDecoration: 'underline', color: 'primary.main' } : {} }}
                              onClick={() => stop.stop_type === 'job' && stop.ticket_id && navigate(`/tickets?id=${stop.ticket_id}`)}>{stop.ticket_id && returnTripIds.has(stop.ticket_id) ? '⚠ ' : ''}{stop.title}{stop.ticket_id && returnTripIds.has(stop.ticket_id) ? <Chip label="RETURN TRIP" size="small" sx={{ ml: 1, height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#fff7ed', color: '#c2410c', border: '1px solid #fdba74' }} /> : null}</Typography>
                            {stop.stop_type === 'job' && stop.notes && (
                              <Chip label={stop.notes.split(' - ')[1] || ''} size="small" sx={{ height: 20, fontSize: 11 }} />
                            )}
                          </Box>
                          {stop.address && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationIcon sx={{ fontSize: 12 }} />{stop.address}
                            </Typography>
                          )}
                          {stop.stop_type === 'end_of_day' && stop.notes && (
                            <Typography variant="caption" color="text.secondary">{stop.notes}</Typography>
                          )}
                        </Box>

                        {/* Time block */}
                        <Box sx={{ textAlign: 'right', minWidth: 90 }}>
                          <Typography variant="subtitle2" fontWeight={700} color={color}>
                            {fmtTime(stop.arrive_at)}
                          </Typography>
                          {stop.finish_at && stop.arrive_at && stop.stop_type !== 'end_of_day' && (
                            <Typography variant="caption" color="text.secondary">
                              → {fmtTime(stop.finish_at)}
                            </Typography>
                          )}
                          {stop.estimated_duration_minutes > 0 && stop.stop_type !== 'end_of_day' && (
                            <Typography variant="caption" display="block" color="text.disabled">
                              {stop.estimated_duration_minutes} min
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* ── RIGHT: Map ── */}
        {showMap && !isMobile && (
          <Box sx={{ flex: 1, position: 'relative' }}>
            {mapPoints.length > 0 ? (
              <MapContainer
                center={mapPoints[0]}
                zoom={11}
                style={{ width: '100%', height: '100%' }}
                zoomControl={false}
              >
                <FitBounds points={mapPoints} />

                {/* Start position marker */}
                {schedule?.start_lat && schedule?.start_lng && (
                  <Marker position={[schedule.start_lat, schedule.start_lng]} icon={createStartMarker()}>
                    <Popup>
                      <b>Start: {schedule.start_address}</b><br />
                      Depart {fmtTime(schedule.stops[0]?.depart_at || null)}
                    </Popup>
                  </Marker>
                )}

                {/* Route polylines */}
                {schedule?.stops.map((stop, idx) => {
                  if (!stop.travel_from_previous_polyline?.length) return null;
                  return (
                    <Polyline
                      key={`route-${idx}`}
                      positions={stop.travel_from_previous_polyline as [number, number][]}
                      pathOptions={{
                        color: routeColors[idx % routeColors.length],
                        weight: 4,
                        opacity: 0.8,
                        dashArray: stop.stop_type === 'end_of_day' ? '8, 8' : undefined,
                      }}
                    />
                  );
                })}

                {/* Stop markers */}
                {schedule?.stops.map((stop, idx) => {
                  if (!stop.lat || !stop.lng) return null;
                  const jobNum = stop.stop_type === 'job' ? jobStops.findIndex(j => j.ticket_id === stop.ticket_id) : idx;
                  return (
                    <Marker
                      key={`stop-${idx}`}
                      position={[stop.lat, stop.lng]}
                      icon={createStopMarker(stop.stop_type, jobNum)}
                    >
                      <Popup>
                        <b>{stop.title}</b><br />
                        {stop.address && <><small>{stop.address}</small><br /></>}
                        Arrive: {fmtTime(stop.arrive_at)}<br />
                        {stop.finish_at && stop.stop_type !== 'end_of_day' && <>Finish: {fmtTime(stop.finish_at)}<br /></>}
                        {stop.travel_from_previous_miles > 0 && (
                          <small>{fmtMiles(stop.travel_from_previous_miles)} · {fmtDuration(stop.travel_from_previous_seconds)} drive</small>
                        )}
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.disabled' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <MapIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                  <Typography>Build a schedule to see routes on map</Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Mobile map drawer */}
        {isMobile && (
          <Drawer anchor="bottom" open={showMap} onClose={() => setShowMap(false)}
            PaperProps={{ sx: { height: '70vh', borderRadius: '16px 16px 0 0' } }}>
            <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ pl: 1 }}>Route Map</Typography>
              <IconButton onClick={() => setShowMap(false)}><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ flex: 1 }}>
              {mapPoints.length > 0 && (
                <MapContainer center={mapPoints[0]} zoom={11} style={{ width: '100%', height: '100%' }}>
                  <FitBounds points={mapPoints} />
                  {schedule?.stops.map((stop, idx) => {
                    if (!stop.travel_from_previous_polyline?.length) return null;
                    return <Polyline key={`m-route-${idx}`} positions={stop.travel_from_previous_polyline as [number, number][]}
                      pathOptions={{ color: routeColors[idx % routeColors.length], weight: 4, opacity: 0.8 }} />;
                  })}
                  {schedule?.stops.map((stop, idx) => {
                    if (!stop.lat || !stop.lng) return null;
                    const jn = stop.stop_type === 'job' ? jobStops.findIndex(j => j.ticket_id === stop.ticket_id) : idx;
                    return <Marker key={`m-stop-${idx}`} position={[stop.lat, stop.lng]}
                      icon={createStopMarker(stop.stop_type, jn)} />;
                  })}
                </MapContainer>
              )}
            </Box>
          </Drawer>
        )}
      </Box>

      <Snackbar open={!!snackMsg} autoHideDuration={4000} onClose={() => setSnackMsg('')}
        message={snackMsg} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
